"use client";

import { useEffect, useRef } from "react";
import { useAction, useConvexAuth, useQuery } from "convex/react";
import { api } from "@repo/backend/convex/_generated/api";
import { takePendingCheckout } from "@/lib/pendingCheckout";

/**
 * When a signed-out visitor clicks a "Get pass" button, PricingTableCustom
 * stashes the chosen plan and sends them to sign up. Once they're authenticated
 * AND onboarded, this resumes that checkout automatically against their new
 * (linked) account — so they land on Stripe having picked a plan while logged
 * out, without any pay-first/link-later plumbing. Fires at most once.
 */
const PendingCheckoutResumer = () => {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const onboarding = useQuery(api.onboarding.getMyOnboarding);
  const createCheckout = useAction(api.autumn.createCheckout);
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (isLoading || onboarding === undefined) return;
    // Wait until they have a real, set-up account so the Autumn customer is the
    // linked user (mirrors ReferralAutoRedeemer).
    if (!isAuthenticated || !onboarding?.completed) return;
    if (attemptedRef.current) return;
    attemptedRef.current = true;

    const pending = takePendingCheckout();
    if (!pending) return;

    const origin = window.location.origin;
    const ret = encodeURIComponent(pending.returnUrl ?? `${origin}/dashboard/billing`);
    const successUrl = `${origin}/checkout/success?return=${ret}`;

    void createCheckout({
      planId: pending.planId,
      successUrl,
      rewardId: pending.rewardId,
    })
      .then(({ paymentUrl }) => {
        window.location.href = paymentUrl ?? successUrl;
      })
      .catch((e) => console.error("Resuming checkout failed", e));
  }, [isAuthenticated, isLoading, onboarding, createCheckout]);

  return null;
};

export default PendingCheckoutResumer;
